import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox,
  RadioGroup,
  Radio,
  Card,
  CardContent,
  Link as MuiLink,
  Alert,
  Button,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import { isProfileReadyForSubmission, showErrorMessage } from "../../../utils";
import ProfileStepInformationStatuses from "../../Profile/Components/ProfileStepInformationStatuses";

export default function Submit({ onDirtyChange, onValidChange, onSave, onSubmit, onBack, loading }: any) {
  const [term, setTerm] = useState(false);
  const [submitApproval, setSubmitApproval] = useState<string | null>(null);
  const profileUser = useSelector((state: any) => state.profile.user);
  const [subjectsCompleted, setSubjectsCompleted] = useState(false);
  const [billingCompleted, setBillingCompleted] = useState(false);

  React.useEffect(() => {
    onDirtyChange?.(false);
  }, []);

  const checkProfileReadiness = () => {
    return isProfileReadyForSubmission(profileUser, subjectsCompleted, billingCompleted);
  };

  const handleSubmit = () => {
    const isReadyForSubmission = checkProfileReadiness();
    
    if (submitApproval == null) {
      return showErrorMessage(
        "Please select if you want to submit your profile for approval or not."
      );
    }
    
    if (submitApproval === "true") {
      if (!isReadyForSubmission) {
        return showErrorMessage(
          "Your profile appears to be incomplete. To successfully submit it for approval, please provide the required information across all steps."
        );
      }
      if (!term) {
        return showErrorMessage('Please confirm the "Terms and Conditions"');
      }
      onSubmit?.();
    } else {
      onSave?.();
    }
  };

  useEffect(() => {
    // Submit step is always valid (validation happens in handleSubmit)
    onValidChange?.(true);
  }, [onValidChange]);

  (Submit as any).save = async () => {
    onDirtyChange?.(false);
  };

  return (
    <Box>
      <Card
        elevation={0}
        sx={{
          border: "1px solid #e9e9ec",
          borderRadius: 2,
        }}
      >
        <CardContent>
          <FormGroup
            aria-labelledby="submit-approval-label"
          >
            <FormControl fullWidth sx={{ mb: 3 }}>
              <FormLabel
                id="submit-approval-label"
                sx={{ mb: 2, fontWeight: 600, fontSize: "1rem" }}
              >
                Would you like to <b>submit your profile</b> for approval?
              </FormLabel>
              <RadioGroup
                row
                value={submitApproval || ""}
                onChange={(e) => setSubmitApproval(e.target.value)}
              >
                <FormControlLabel value="false" control={<Radio />} label="No" />
                <FormControlLabel value="true" control={<Radio />} label="Yes" />
              </RadioGroup>
            </FormControl>

            <FormControlLabel
              control={
                <Checkbox
                  checked={term}
                  onChange={(e) => setTerm(e.target.checked)}
                  sx={{
                    color: "#712d94",
                    "&.Mui-checked": {
                      color: "#712d94",
                    },
                  }}
                />
              }
              label={
                <Typography sx={{ fontSize: "1rem" }}>
                  I agree to the{" "}
                  <MuiLink
                    href="/terms"
                    target="_blank"
                    sx={{
                      color: "#712d94",
                      textDecoration: "underline",
                    }}
                  >
                    Terms and Conditions
                  </MuiLink>
                </Typography>
              }
              sx={{ mb: 3 }}
            />
          </FormGroup>

          {submitApproval === "true" && (
            <>
              {!checkProfileReadiness() && (
                <Alert severity="warning" sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                    Missing information!
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#da1e28", mb: 1 }}>
                    Your profile is not live.
                  </Typography>
                  <Typography variant="body2">
                    The red 'incomplete' indicates, requires information. Please fill
                    in the missing information before submitting to be reviewed. You
                    have the option to save your progress and return later to
                    complete it. The decision is yours!
                  </Typography>
                </Alert>
              )}
              <Box sx={{ maxWidth: "700px", my: 2 }}>
                <ProfileStepInformationStatuses
                  onSubjectsStatusChange={setSubjectsCompleted}
                  onBillingStatusChange={setBillingCompleted}
                />
              </Box>
            </>
          )}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 4 }}>
            <Button
              variant="outlined"
              size="large"
              onClick={onBack}
              disabled={loading}
            >
              Back
            </Button>
            <Button
              variant="contained"
              size="large"
              onClick={handleSubmit}
              disabled={loading || !submitApproval}
            >
              {loading && (
                <CircularProgress
                  size={20}
                  sx={{ color: "white", mr: 1 }}
                />
              )}
              {submitApproval === "true" ? "Submit" : "Save"}
            </Button>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}
